import { useState } from "react";
import { App, Spin } from "antd";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { WorkingDay } from "../../../../api/configuration";
import { useConfiguration, useUpdateWorkingHours } from "../../../../hooks/useConfiguration";
import { getApiErrorMessage } from "../../../../utils/translations";
import type { ConfigOutletContext } from "../../Configuration";
import { SettingsSection } from "../ordering-settings/SettingsSection";
import WorkingHoursTopBar from "./WorkingHoursTopBar";
import { WorkingDayRow } from "./WorkingDayRow";
import { WorkingHoursGuide } from "./WorkingHoursGuide";
import { DEFAULT_WEEK, isSameWeek, isWeekValid, workingDayErrorOf } from "./workingHoursForm";

// Понеделник първи — като в настройката.
const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

/**
 * Конфигурация → Работно време: „от – до" или почивен ден за всеки ден от
 * седмицата (таблица `configuration`, ред `working_hours`; същото право като
 * „Маркиране" — ordering-settings). Ползва се САМО за часовете по
 * подразбиране в справките с час (`lib/businessDay.ts`) и за часовете на
 * формата за резервации (`reservations/logic/workingSlots.ts`).
 *
 * За разлика от „Маркиране" записът е с бутон, а не на всеки клик: денят
 * след полунощ се проверява срещу следващия, а седмицата е един PUT.
 */
const WorkingHoursPage = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isNarrow, onOpenMobileMenu } = useOutletContext<ConfigOutletContext>();
  const { configuration, isLoaded } = useConfiguration();
  const { mutate: saveWorkingHours, isPending: isSaving } = useUpdateWorkingHours();

  // Още не е записано → формата тръгва от седмицата по подразбиране и
  // „Запази" е активен и без промяна.
  // `?? null` — /user/info от по-стар BE изобщо няма полето.
  const isConfigured = (configuration.workingHours ?? null) !== null;
  const savedWeek = configuration.workingHours ?? DEFAULT_WEEK;
  const [draft, setDraft] = useState<WorkingDay[] | null>(null);
  const week = draft ?? savedWeek;
  const isDirty = draft !== null && !isSameWeek(draft, savedWeek);
  const canSave = !isSaving && isWeekValid(week) && (isDirty || !isConfigured);

  const changeDay = (index: number, day: WorkingDay) =>
    setDraft(week.map((current, dayIndex) => (dayIndex === index ? day : current)));

  const save = () =>
    saveWorkingHours(week, {
      onSuccess: () => {
        setDraft(null);
        message.success(t("workingHours.saveSuccess"));
      },
      onError: (error) => message.error(getApiErrorMessage(error, t("workingHours.saveError"))),
    });

  return (
    <div className="flex h-full flex-col bg-canvas">
      <WorkingHoursTopBar isNarrow={isNarrow} onOpenMobileMenu={onOpenMobileMenu} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!isLoaded ? (
          <div className="flex justify-center p-12">
            <Spin size="large" />
          </div>
        ) : (
          <div className="mx-auto flex min-h-full max-w-[1100px] flex-col gap-4 bg-canvas px-3.5 pb-6 pt-4 sm:px-7 sm:pb-8 sm:pt-6">
            <WorkingHoursGuide />

            <SettingsSection
              kicker={t("workingHours.sectionKicker")}
              title={t("workingHours.sectionTitle")}
              count={week.length}
            >
              {week.map((day, index) => (
                <WorkingDayRow
                  key={WEEKDAY_KEYS[index]}
                  name={t(`workingHours.days.${WEEKDAY_KEYS[index]}`)}
                  day={day}
                  error={workingDayErrorOf(week, index)}
                  disabled={isSaving}
                  onChange={(next) => changeDay(index, next)}
                />
              ))}
            </SettingsSection>

            <div className="flex flex-wrap items-center justify-end gap-2.5">
              {isDirty && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => setDraft(null)}
                  className="h-[42px] cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-5 text-[13.5px] font-bold text-ink transition-colors duration-150 hover:bg-fill disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("workingHours.discard")}
                </button>
              )}
              <button
                type="button"
                disabled={!canSave}
                onClick={save}
                className="flex h-[42px] cursor-pointer items-center gap-2 rounded-[10px] border-none bg-brand px-[22px] text-[13.5px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand"
              >
                {isSaving && <Spin size="small" />}
                {t("workingHours.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkingHoursPage;
